import { isComputeBehavior } from './behaviors.js';
import { parseFormulaTemplate } from './formula-templates.js';
// ---------------------------------------------------------------------------
// Static sample data pools
// ---------------------------------------------------------------------------
const COMPANY_NAMES = [
    'Acme Corporation',
    'Pinnacle Supply Co.',
    'Meridian Industries',
    'Apex Logistics',
    'Summit Materials LLC',
];
const PERSON_NAMES = [
    'Jane Doe',
    'John Smith',
    'Maria Garcia',
    'Robert Chen',
    'Sarah Patel',
];
const PRODUCT_DESCRIPTIONS = [
    'Industrial-grade steel components for structural use',
    'Monthly maintenance services per contract agreement',
    'Bulk raw material shipment — standard pallet configuration',
    'Professional consulting services, Q2 engagement',
    'Equipment parts and replacement hardware',
];
// ---------------------------------------------------------------------------
// Heuristic detectors
// ---------------------------------------------------------------------------
function isIdField(name) {
    return /number|num\b|_no\b|_ref\b|_id\b/i.test(name);
}
function isAmountField(name) {
    return /amount|total|price|cost|fee/i.test(name);
}
function isQtyField(name) {
    return /count\b|qty\b|quantity/i.test(name);
}
function isCompanyNameField(name) {
    return /vendor|company|org\b|customer|client/i.test(name) && /name/i.test(name);
}
function isPersonNameField(name) {
    return /name/i.test(name);
}
function isDescriptionField(name) {
    return /desc\b|description/i.test(name);
}
// ---------------------------------------------------------------------------
// Compute@<stage> static-literal evaluation (#53)
// ---------------------------------------------------------------------------
/**
 * If `field` has a compute behavior whose formula resolves to a static
 * literal (quoted string, number, or boolean), return that value as a
 * typed Value. Returns null when no static-compute is attached, so the
 * caller can fall back to the standard heuristic/dataType generators.
 *
 * This makes the export preview reflect what the operator actually
 * configured (e.g. `compute(static "MARKETING")@pre-export`) instead of
 * the generic placeholder `"Sample Text N"`.
 */
function evaluateStaticCompute(field) {
    for (const stageBehaviors of Object.values(field.stageMap)) {
        for (const b of stageBehaviors ?? []) {
            if (!isComputeBehavior(b) || b.dependsOn.length > 0)
                continue;
            const template = parseFormulaTemplate(b.formula.source);
            if (!template || template.kind !== 'static')
                continue;
            const coerced = coerceStaticValue(template.value);
            if (coerced !== null)
                return coerced;
        }
    }
    return null;
}
function coerceStaticValue(raw) {
    const trimmed = raw.trim();
    if (trimmed === 'true')
        return true;
    if (trimmed === 'false')
        return false;
    // `null` literal — Value can't represent null, so signal "fall through to
    // heuristics" rather than rendering the string "null" in the preview.
    if (trimmed === 'null')
        return null;
    const stringMatch = trimmed.match(/^(?:"([^"]*)"|'([^']*)')$/);
    if (stringMatch)
        return stringMatch[1] ?? stringMatch[2] ?? '';
    if (/^-?\d+(?:\.\d+)?$/.test(trimmed))
        return parseFloat(trimmed);
    return trimmed;
}
// ---------------------------------------------------------------------------
// Single-field value generator
// ---------------------------------------------------------------------------
/**
 * Name-only heuristic value for a field, or null when no name heuristic
 * applies (caller falls back to dataType defaults or a generic string).
 * Shared by {@link generateFieldValue} and {@link sampleValueForFieldName}
 * so the two can never disagree on what a "vendorName" or "totalAmount"
 * looks like.
 */
function nameHeuristicValue(name, variant) {
    const n = name.toLowerCase();
    if (isIdField(n)) {
        const year = new Date().getFullYear();
        const seq = String(1 + variant).padStart(3, '0');
        return `INV-${year}-${seq}`;
    }
    // Return amounts as numbers regardless of any declared dataType.
    if (isAmountField(n))
        return parseFloat((1250 + variant * 100).toFixed(2));
    if (isQtyField(n))
        return (variant + 1) * 3;
    if (isCompanyNameField(n))
        return COMPANY_NAMES[variant % COMPANY_NAMES.length];
    if (isPersonNameField(n))
        return PERSON_NAMES[variant % PERSON_NAMES.length];
    if (isDescriptionField(n))
        return PRODUCT_DESCRIPTIONS[variant % PRODUCT_DESCRIPTIONS.length];
    return null;
}
/**
 * Best-effort sample value for a bare field NAME (no FieldContent available) —
 * used by the formula dry-run tester to prefill scaffolded inputs. Applies the
 * name heuristics, defaulting to a generic string when none match.
 */
export function sampleValueForFieldName(name, variant = 0) {
    return nameHeuristicValue(name, variant) ?? `Sample Text ${variant}`;
}
function generateFieldValue(field, variant) {
    // Static compute@<stage> takes precedence over heuristics so the preview
    // reflects what's actually configured rather than placeholder data (#53).
    const computed = evaluateStaticCompute(field);
    if (computed !== null)
        return computed;
    // Smart name detection takes priority over dataType.
    const heuristic = nameHeuristicValue(field.name, variant);
    if (heuristic !== null)
        return heuristic;
    // Fall back to dataType defaults
    switch (field.dataType) {
        case 'text':
            return `Sample Text ${variant}`;
        case 'number':
            return 1250 + variant * 100;
        case 'decimal':
            return parseFloat((1250 + variant * 100).toFixed(2));
        case 'date': {
            const d = new Date();
            d.setDate(d.getDate() - variant);
            return d.toISOString().slice(0, 10);
        }
        case 'boolean':
            return variant % 2 === 0;
        default:
            return `Sample Text ${variant}`;
    }
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Generate a single sample record from a list of field definitions.
 *
 * @param fields  Field definitions from a JobTypeVersionContent
 * @param variant Variant index (default 0) — produces slightly different values
 *                so consecutive calls for different rows look realistic.
 */
export function generateSampleRecord(fields, variant = 0) {
    const record = {};
    for (const field of fields) {
        if (field.scope === 'batch')
            continue;
        record[field.name] = generateFieldValue(field, variant);
    }
    return record;
}
/**
 * Generate a batch of sample records plus special system fields for
 * export live preview.
 *
 * @param fields Field definitions from a JobTypeVersionContent
 * @param count  Number of records to generate (default 2)
 */
export function generateSampleBatch(fields, count = 2) {
    const records = Array.from({ length: count }, (_, i) => generateSampleRecord(fields, i));
    const special = {
        _documentId: 'DOC-00001',
        _batchName: `BATCH-${new Date().getFullYear()}-042`,
        _classification: 'Invoice',
        _pageCount: 3,
        _exportTimestamp: new Date().toISOString(),
        _operatorName: 'Jane Doe',
        _imageUrls: 'https://storage.example.com/pages/001.png',
    };
    return { records, special };
}
//# sourceMappingURL=sample-data.js.map